// IA nas páginas: ações sobre o texto selecionado (melhorar, corrigir,
// traduzir, mudar o tom…), escrever no cursor, perguntar sobre tudo o que
// está no Ordo (com as fontes) e transformar um texto em tarefas de um banco.

import { Marked } from "marked";
import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { VoiceSession } from "./voice.js";
import { el, popup, dialog, toast, titleOf } from "./dom.js";
import { iconNode, iconText, ui } from "./icons.js";

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const md = new Marked({ gfm: true, breaks: true, renderer: { html: ({ text }) => escapeHtml(text) } });
// O HTML cru vem escapado; links e imagens só com esquemas seguros.
export function mdToHtml(text) {
  const doc = new DOMParser().parseFromString(`<body>${md.parse(String(text || ""))}</body>`, "text/html");
  for (const n of doc.body.querySelectorAll("*")) {
    for (const attr of [...n.attributes]) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "style") n.removeAttribute(attr.name);
      if ((name === "href" || name === "src") && !/^(https?:|mailto:|ordo:page:)/i.test(attr.value.trim())) n.removeAttribute(attr.name);
    }
  }
  return doc.body.innerHTML;
}

// Links "ordo:page:ID" nas respostas abrem a página.
export function renderAnswer(box, text, onOpen) {
  box.innerHTML = mdToHtml(text);
  for (const a of box.querySelectorAll("a[href]")) {
    const m = /^ordo:page:([0-9a-f-]{36})$/i.exec(a.getAttribute("href"));
    a.addEventListener("click", (e) => {
      e.preventDefault();
      if (m) { onOpen?.(); router.openPage(m[1]); } else window.open(a.href, "_blank");
    });
    if (m) a.classList.add("ordo-link");
  }
}

const ACTIONS = [
  { id: "improve", label: "Melhorar a escrita", icon: "✨" },
  { id: "fix", label: "Corrigir ortografia e gramática", icon: "✓" },
  { id: "shorter", label: "Deixar mais curto", icon: "↘" },
  { id: "longer", label: "Deixar mais longo", icon: "↗" },
  { id: "simplify", label: "Simplificar a linguagem", icon: "☺" },
  { id: "explain", label: "Explicar", icon: "?" },
  { id: "summarize", label: "Resumir", icon: "≡" },
  { id: "action_items", label: "Encontrar tarefas", icon: "☑" },
];
const LANGS = ["inglês", "espanhol", "português", "francês", "italiano", "alemão"];
const TONES = ["profissional", "casual", "direto", "amigável", "confiante", "formal"];

// Menu da IA na barra de formatação (texto selecionado).
export function aiSelectionMenu(editor, anchor) {
  const { from, to } = editor.state.selection;
  const text = editor.state.doc.textBetween(from, to, "\n\n");
  const ctx = editor.ordoCtx || {};
  const prompt = el("input", { class: "pop-input", placeholder: "Peça à IA para editar ou escrever…" });
  const run = (action, extra = {}) => {
    box.close();
    runAction(editor, { action, text, pageId: ctx.pageId, ...extra }, { from, to, replace: true });
  };
  const item = (label, ic, onClick) => el("button", { class: "menu-item", onmousedown: (e) => e.preventDefault(), onclick: onClick }, el("span", { class: "mi-icon" }, ic), el("span", {}, label));
  const sub = (label, ic, values, key, action) => {
    const b = item(label, ic, () => {
      popup(b, el("div", { class: "menu" }, values.map((v) => item(v[0].toUpperCase() + v.slice(1), "", () => run(action, { [key]: v })))), { align: "left", className: "menu" });
    });
    b.append(el("span", { class: "mi-hint" }, "›"));
    return b;
  };
  const box = popup(anchor, el("div", { class: "ai-menu" },
    el("form", { class: "ai-prompt", onsubmit: (e) => { e.preventDefault(); if (prompt.value.trim()) run("write", { prompt: prompt.value.trim() }); } }, ui("ai"), prompt),
    el("div", { class: "menu-sep" }),
    ...ACTIONS.map((a) => item(a.label, a.icon, () => run(a.id))),
    sub("Traduzir para", "🌐", LANGS, "language", "translate"),
    sub("Mudar o tom", "🎚", TONES, "tone", "tone")), { className: "menu ai-pop" });
  requestAnimationFrame(() => prompt.focus());
}

// Slash "/IA": escreve no cursor (com pedido) ou age sobre a página inteira.
export function aiAtCursor(editor, ctx, action) {
  const pos = editor.state.selection.from;
  if (action !== "write" && action !== "brainstorm") {
    runAction(editor, { action, pageId: ctx.pageId }, { from: pos, to: pos, replace: false });
    return;
  }
  const coords = editor.view.coordsAtPos(pos);
  const input = el("input", { class: "pop-input", placeholder: action === "brainstorm" ? "Ideias sobre…" : "O que a IA deve escrever? (ex.: e-mail para o cliente sobre o atraso)" });
  const box = popup({ x: coords.left, y: coords.bottom }, el("form", { class: "ai-prompt wide", onsubmit: (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) return;
    box.close();
    runAction(editor, { action, prompt: q, text: action === "brainstorm" ? q : undefined, pageId: ctx.pageId }, { from: pos, to: pos, replace: false });
  } }, ui("ai"), input, el("button", { type: "submit", class: "small primary" }, "Gerar")), { className: "ai-pop" });
  requestAnimationFrame(() => input.focus());
}

// Chama a IA e mostra o resultado com as opções de usar ou descartar.
async function runAction(editor, body, place) {
  const coords = editor.view.coordsAtPos(Math.min(place.to, editor.state.doc.content.size));
  const out = el("div", { class: "ai-out" }, el("p", { class: "muted" }, el("span", { class: "spinner" }), " A IA está escrevendo…"));
  const actions = el("div", { class: "ai-actions" });
  const box = popup({ x: Math.max(16, coords.left - 40), y: coords.bottom + 4 }, el("div", { class: "ai-result" }, out, actions), { className: "ai-pop result" });
  let res;
  try {
    res = await api("POST", "/ai", body, { timeout: 180000 });
  } catch (err) {
    out.replaceChildren(el("p", { class: "error" }, err.message));
    actions.replaceChildren(el("button", { class: "small", onclick: () => box.close() }, "Fechar"));
    return;
  }
  renderAnswer(out, res.text);
  const html = mdToHtml(res.text);
  const insertAt = (pos) => editor.chain().focus().insertContentAt(pos, html).run();
  actions.replaceChildren(
    place.replace && place.from !== place.to ? el("button", { class: "small primary", onclick: () => {
      box.close();
      editor.chain().focus().deleteRange({ from: place.from, to: place.to }).run();
      insertAt(place.from);
    } }, "Substituir") : null,
    el("button", { class: place.replace && place.from !== place.to ? "small" : "small primary", onclick: () => {
      box.close();
      const $to = editor.state.doc.resolve(Math.min(place.to, editor.state.doc.content.size));
      const after = $to.depth ? $to.after(1) : place.to;
      insertAt(place.replace ? after : place.from);
    } }, place.replace ? "Inserir abaixo" : "Inserir"),
    el("button", { class: "small", onclick: () => { navigator.clipboard?.writeText(res.text); toast("Copiado."); } }, "Copiar"),
    el("button", { class: "small", onclick: () => { box.close(); runAction(editor, body, place); } }, "Tentar de novo"),
    el("button", { class: "small link", onclick: () => box.close() }, "Descartar"));
}

// ---------- Perguntar ao Ordo ----------

const sourcesNode = (sources) => el("div", { class: "ask-sources" }, el("span", { class: "muted small" }, "Fontes consultadas: "),
  sources.map((s) => el("button", { class: "chip", onclick: () => router.openPage(s.id) }, iconNode(store.node(s.id)?.icon, s.kind === "database" ? "▦" : "📄"), " ", titleOf(s))));

export function renderAsk(host, { initial, workspace, voice } = {}) {
  const q = el("textarea", { class: "ask-input", rows: 2, placeholder: "Pergunte sobre páginas, tarefas e reuniões. Ex.: o que está bloqueado no quadro do DEV? O que foi combinado com o cliente na última reunião?" });
  const spaceSel = el("select", { class: "ask-scope" },
    el("option", { value: "" }, "Em tudo o que eu vejo"),
    ...store.state.workspaces.map((w) => el("option", { value: w.id }, `Só no ambiente ${iconText(w.icon)} ${w.name}`)));
  if (workspace) spaceSel.value = workspace;
  const thread = el("div", { class: "ask-thread" });
  const ask = async () => {
    const question = q.value.trim();
    if (!question) return;
    q.value = "";
    const answer = el("div", { class: "ask-answer" }, el("p", { class: "muted" }, el("span", { class: "spinner" }), " Procurando no Ordo e pensando…"));
    const item = el("div", { class: "ask-item" }, el("div", { class: "ask-q" }, question), answer);
    thread.prepend(item);
    try {
      const res = await api("POST", "/ai/ask", { question, workspaceId: spaceSel.value || undefined }, { timeout: 180000 });
      renderAnswer(answer, res.answer);
      if (res.sources?.length) answer.append(sourcesNode(res.sources));
    } catch (err) {
      answer.replaceChildren(el("p", { class: "error" }, err.message));
    }
  };
  q.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } });
  const voiceSlot = el("div");
  const voiceBtn = el("button", { class: "voice-start", title: "Conversar por voz", onclick: () => {
    voiceBtn.disabled = true;
    voiceSlot.replaceChildren(voicePanel({ thread, workspaceId: spaceSel.value, onClose: () => {
      voiceSlot.replaceChildren();
      voiceBtn.disabled = false;
    } }));
  } }, ui("voice"), " Conversar por voz");
  host.replaceChildren(el("div", { class: "ask-view" },
    el("h1", {}, ui("ai", 26), " Perguntar ao Ordo"),
    el("p", { class: "muted" }, "A IA procura nas páginas, nos quadros e nas reuniões que você pode ver e responde com os links de onde tirou cada informação."),
    el("div", { class: "ask-box" }, q, el("div", { class: "ask-row" }, spaceSel, el("div", { class: "ask-buttons" }, voiceBtn, el("button", { class: "primary", onclick: ask }, "Perguntar")))),
    voiceSlot,
    thread));
  if (initial) { q.value = initial; ask(); }
  if (voice) voiceBtn.click();
  else requestAnimationFrame(() => q.focus());
}

// Painel da conversa por voz: as falas e respostas também ficam escritas na conversa.
const VOICE_STATUS = {
  starting: "Ligando o microfone…",
  listening: "Pode falar, estou ouvindo.",
  hearing: "Ouvindo…",
  transcribing: "Entendendo o que você disse…",
  thinking: "Procurando no Ordo e pensando…",
  speaking: "Respondendo. Fale por cima para interromper.",
};

function voicePanel({ thread, workspaceId, onClose }) {
  const orb = el("div", { class: "voice-orb" });
  const status = el("div", { class: "voice-status" }, VOICE_STATUS.starting);
  const errorLine = el("div", { class: "voice-error small" });
  const muteBtn = el("button", { class: "small", onclick: () => {
    session.setMuted(!session.muted);
    muteBtn.replaceChildren(ui(session.muted ? "micOff" : "mic", 14), session.muted ? " Ativar microfone" : " Silenciar");
    panel.classList.toggle("muted", session.muted);
  } }, ui("mic", 14), " Silenciar");
  const sendBtn = el("button", { class: "small", onclick: () => session.sendNow() }, ui("send", 14), " Enviar agora");
  const stopBtn = el("button", { class: "small", onclick: () => session.interrupt() }, ui("hand", 14), " Interromper");
  const bargeKey = "ordo.voiceBargeIn";
  let barge = true;
  try { barge = localStorage.getItem(bargeKey) !== "0"; } catch { /* sem armazenamento */ }
  const bargeBox = el("input", { type: "checkbox", checked: barge, onchange: (e) => {
    session.bargeIn = e.target.checked;
    try { localStorage.setItem(bargeKey, e.target.checked ? "1" : "0"); } catch { /* sem armazenamento */ }
  } });
  const close = () => {
    session.stop();
    document.removeEventListener("keydown", onKey);
    onClose();
  };
  const onKey = (e) => { if (e.key === "Escape") close(); };
  const panel = el("div", { class: "voice-panel", "data-state": "starting" },
    el("div", { class: "voice-main" }, orb, el("div", { class: "voice-info" }, status, errorLine)),
    el("div", { class: "voice-actions" }, muteBtn, sendBtn, stopBtn,
      el("button", { class: "small primary", onclick: close }, ui("x", 14), " Encerrar")),
    el("label", { class: "voice-option muted small", title: "Desligue se o Ordo se interromper sozinho (alto-falante alto, sem fone)." },
      bargeBox, " Interromper o Ordo quando eu falar"));

  const session = new VoiceSession({
    workspaceId,
    alive: () => panel.isConnected,
    onState: (state) => {
      panel.dataset.state = state;
      status.textContent = VOICE_STATUS[state] || "";
      if (state === "speaking" && (!session.bargeIn)) status.textContent = "Respondendo…";
      if (state === "hearing") errorLine.textContent = "";
    },
    onLevel: (level) => orb.style.setProperty("--lvl", Math.min(1, level * 12).toFixed(2)),
    onError: (msg) => { errorLine.textContent = msg; },
    onTurn: (question) => {
      const answer = el("div", { class: "ask-answer" }, el("p", { class: "muted" }, el("span", { class: "spinner" }), " Pensando…"));
      const textBox = el("div");
      const item = el("div", { class: "ask-item voice-item" }, el("div", { class: "ask-q" }, ui("mic", 14), " ", question), answer);
      thread.prepend(item);
      let sources = null;
      return {
        sources: (list) => { sources = list.length ? sourcesNode(list) : null; },
        text: (text, done) => {
          renderAnswer(textBox, text);
          answer.replaceChildren(textBox, done && sources ? sources : null);
        },
        error: (msg) => answer.replaceChildren(el("p", { class: "error" }, msg)),
        remove: () => item.remove(),
      };
    },
  });
  session.bargeIn = barge;
  document.addEventListener("keydown", onKey);
  session.start().catch((err) => {
    session.stop();
    document.removeEventListener("keydown", onKey);
    toast(/Permission|NotAllowed/i.test(err.name + err.message) ? "O Ordo não tem permissão para usar o microfone." : `Não foi possível ligar o microfone: ${err.message}`);
    onClose();
  });
  return panel;
}

// ---------- Texto -> tarefas num banco de dados ----------

export function extractTasksDialog(pageId) {
  const dbs = store.databases().filter((d) => store.canEdit(d));
  if (!dbs.length) return toast("Crie um banco de dados (quadro) antes, para receber as tarefas.");
  const last = localStorage.getItem("ordo.tasksDb");
  const sel = el("select", {}, dbs.map((d) => el("option", { value: d.id, selected: d.id === last }, `${iconText(d.icon, "▦")} ${titleOf(d)}`)));
  const list = el("div", { class: "task-preview" });
  const go = el("button", { class: "primary", disabled: true }, "Criar tarefas");
  let tasks = [];
  let schema = null;
  const load = async () => {
    list.replaceChildren(el("p", { class: "muted" }, el("span", { class: "spinner" }), " A IA está lendo a página…"));
    go.disabled = true;
    try {
      const db = await store.load(sel.value);
      schema = db.page.schema;
      const fields = (schema.properties || []).filter((p) => ["select", "status", "date", "person", "multi_select"].includes(p.type))
        .map((p) => ({ id: p.id, name: p.name, type: p.type, options: p.type === "person" ? (store.state.users || []).map((u) => u.name) : p.options?.map((o) => o.name) }));
      const res = await api("POST", "/ai/tasks", { pageId, fields }, { timeout: 180000 });
      tasks = res.tasks.map((t) => ({ ...t, on: true }));
      draw();
    } catch (err) {
      list.replaceChildren(el("p", { class: "error" }, err.message));
    }
  };
  const draw = () => {
    list.replaceChildren(...(tasks.length ? tasks.map((t) => el("label", { class: "task-row" },
      el("input", { type: "checkbox", checked: t.on, onchange: (e) => { t.on = e.target.checked; go.disabled = !tasks.some((x) => x.on); } }),
      el("div", {}, el("strong", {}, t.title), t.description ? el("div", { class: "muted small" }, t.description) : null,
        el("div", { class: "muted small" }, Object.entries(t.props || {}).map(([k, v]) => {
          const p = schema.properties.find((x) => x.id === k);
          return p ? `${p.name}: ${[].concat(v).join(", ")}` : null;
        }).filter(Boolean).join(" · ")))))
      : [el("p", { class: "muted" }, "A IA não encontrou tarefas nesta página.")]));
    go.disabled = !tasks.some((x) => x.on);
  };
  go.addEventListener("click", () => {
    const users = store.state.users || [];
    let n = 0;
    for (const t of tasks.filter((x) => x.on)) {
      const props = {};
      for (const [k, v] of Object.entries(t.props || {})) {
        const p = schema.properties.find((x) => x.id === k);
        if (!p || v === null || v === "") continue;
        if (p.type === "person") props[k] = [].concat(v).map((name) => users.find((u) => u.name === name)?.id).filter(Boolean);
        else if (p.type === "multi_select") props[k] = [].concat(v);
        else props[k] = v;
      }
      store.create({ parent_id: sel.value, title: t.title, props,
        content: t.description ? { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: t.description }] }] } : null });
      n++;
    }
    localStorage.setItem("ordo.tasksDb", sel.value);
    dlg.close();
    toast(`${n} tarefa(s) criada(s) em ${titleOf(store.node(sel.value))}.`);
  });
  sel.addEventListener("change", load);
  const dlg = dialog((close) => [
    el("h3", {}, "✨ Transformar em tarefas"),
    el("p", { class: "muted small" }, "A IA lê esta página e sugere as tarefas. Escolha o quadro e desmarque o que não quiser."),
    el("label", { class: "field" }, "Criar no banco de dados", sel),
    list,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"), go),
  ], { wide: true });
  load();
}
